import { parseCloudRunError } from '../components/msiDriftReviewHelpers';
import { callBackend } from './api';

const SAFE_ERROR_MESSAGES = Object.freeze({
    request_invalid: 'The request could not be completed. Refresh Settings and try again.',
    feature_unavailable: 'Linked Excel Watchlist is not available in this environment.',
    connection_required: 'Connect a Microsoft organizational account first.',
    reconnect_required: 'Microsoft authorization needs to be renewed. Connect again.',
    oauth_state_invalid: 'The Microsoft authorization request is no longer valid. Start Connect again.',
    oauth_state_expired: 'The Microsoft authorization request expired. Start Connect again.',
    account_not_organizational: 'Use a Microsoft work or school account. Personal Microsoft accounts are not supported.',
    account_mismatch: 'Reconnect with the same Microsoft organizational account used for this connection.',
    permission_missing: 'Microsoft did not grant the required Files.Read permission. Ask your tenant administrator about consent policy, then connect again.',
    sharing_url_invalid: 'Paste a Microsoft 365 organizational OneDrive or SharePoint Excel “Copy link” URL.',
    source_unavailable: 'The linked workbook is unavailable. Check access or choose the workbook again.',
    source_forbidden: 'The connected Microsoft account cannot access this workbook. Check sharing permissions or reconnect.',
    source_rate_limited: 'Microsoft is temporarily limiting workbook requests. Wait a moment and try again.',
    source_timeout: 'The workbook request timed out. Try again.',
    source_too_large: 'This workbook is larger than the supported limit. Use a smaller .xlsx workbook.',
    workbook_invalid: 'Microsoft returned an invalid or incomplete workbook. Check the file and try again.',
    workbook_unsupported: 'This workbook contains unsupported features or is not a supported .xlsx file.',
    worksheet_reselection_required: 'The selected worksheet is no longer available. Choose the workbook and worksheet again.',
    worksheet_invalid: 'The selected worksheet does not contain a valid Watchlist. Choose another worksheet or fix the sheet.',
    watchlist_upload_processing_unknown: 'Unknown issue processing the Watchlist file. Check the worksheet and try again.',
    connection_changed: 'The Microsoft connection changed. Refresh Settings and try again.',
    watchlist_base_changed: 'The Watchlist changed while Excel was checked. Start Sync from Excel again.',
    request_payload_changed: 'This Sync attempt could not be replayed safely. Start Sync from Excel again.',
    watchlist_operation_in_progress: 'A Watchlist update is already in progress. View its current status before syncing again.',
});

const KNOWN_CODES = new Set(Object.keys(SAFE_ERROR_MESSAGES));
const VALIDATION_CODE_PATTERN = /^(?:watchlist_upload|staged_watchlist)_[a-z0-9_]{1,64}$/;

function hasUnsafeControlCharacter(value) {
    return Array.from(value).some((character) => {
        const codePoint = character.codePointAt(0);
        return codePoint <= 0x1f
            || codePoint === 0x7f
            || codePoint === 0x061c
            || codePoint === 0x200e
            || codePoint === 0x200f
            || (codePoint >= 0x202a && codePoint <= 0x202e)
            || (codePoint >= 0x2066 && codePoint <= 0x2069);
    });
}

function safeValidationMessage(code, message) {
    if (!VALIDATION_CODE_PATTERN.test(String(code || '')) || typeof message !== 'string') {
        return null;
    }
    const normalized = message.trim().replace(/\s+/g, ' ');
    if (
        !normalized
        || normalized.length > 400
        || hasUnsafeControlCharacter(normalized)
    ) {
        return null;
    }
    return normalized;
}

export class WatchlistM365ApiError extends Error {
    constructor(code, {
        statusCode = null,
        retryable = false,
        message = null,
        inputValidation = null,
    } = {}) {
        const validationMessage = safeValidationMessage(code, message);
        const safeCode = KNOWN_CODES.has(code) || validationMessage
            ? code
            : 'connection_unavailable';
        super(
            validationMessage || SAFE_ERROR_MESSAGES[safeCode]
            || 'Linked Excel Watchlist is temporarily unavailable. Refresh Settings and try again.',
        );
        this.name = 'WatchlistM365ApiError';
        this.code = safeCode;
        this.statusCode = statusCode;
        this.retryable = Boolean(retryable);
        // A validation error carries the worksheet's skipped rows, as a
        // rejected file upload does.
        this.inputValidation = (
            VALIDATION_CODE_PATTERN.test(String(code || ''))
            && inputValidation
            && typeof inputValidation === 'object'
        ) ? inputValidation : null;
    }
}

function toApiError(error) {
    if (error instanceof WatchlistM365ApiError) {
        return error;
    }
    if (error?.name === 'AbortError') {
        return error;
    }
    const parsed = parseCloudRunError(error);
    const detail = parsed?.detail && typeof parsed.detail === 'object' ? parsed.detail : {};
    const hasExplicitRetryability = typeof detail.retryable === 'boolean';
    return new WatchlistM365ApiError(detail.code || parsed.code, {
        statusCode: parsed.statusCode,
        message: detail.message,
        inputValidation: detail.input_validation,
        retryable: hasExplicitRetryability
            ? detail.retryable
            : parsed.statusCode == null || parsed.statusCode >= 500,
    });
}

async function request(endpoint, method, payload, { signal } = {}) {
    try {
        const response = await callBackend(endpoint, method, payload, { signal });
        if (!response || response.success !== true) {
            const statusCode = Number(response?.status_code);
            const hasExplicitRetryability = typeof response?.retryable === 'boolean';
            throw new WatchlistM365ApiError(response?.code, {
                statusCode: Number.isInteger(statusCode) ? statusCode : null,
                message: response?.message,
                inputValidation: response?.input_validation,
                retryable: hasExplicitRetryability
                    ? response.retryable
                    : Number.isInteger(statusCode) && statusCode >= 500,
            });
        }
        return response;
    } catch (error) {
        throw toApiError(error);
    }
}

export function fetchWatchlistM365Status(options) {
    return request('api/watchlist/m365', 'GET', null, options);
}

export function startWatchlistM365Authorization(options) {
    return request('api/watchlist/m365/authorize', 'POST', {}, options);
}

export function completeWatchlistM365Authorization(authorization, options) {
    return request(
        'api/watchlist/m365/oauth/complete',
        'POST',
        {
            code: authorization.code,
            state: authorization.state,
            browser_session: authorization.browser_session,
        },
        options,
    );
}

export function inspectWatchlistM365Source(sharingUrl, options) {
    return request(
        'api/watchlist/m365/source/inspect',
        'POST',
        { sharing_url: sharingUrl },
        options,
    );
}

export function configureWatchlistM365Source(worksheetName, expectedGeneration, options) {
    return request(
        'api/watchlist/m365/source',
        'PUT',
        { worksheet_name: worksheetName, expected_generation: expectedGeneration },
        options,
    );
}

export function validateWatchlistM365Source(expectedGeneration, options) {
    return request(
        'api/watchlist/m365/validate',
        'POST',
        { expected_generation: expectedGeneration },
        options,
    );
}

// Without a column choice the server answers with the worksheet's column
// check; the user's choice is then sent with the same request id.
export function syncWatchlistM365(requestId, expectedGeneration, columnSelection, options) {
    return request(
        'api/watchlist/m365/sync',
        'POST',
        {
            request_id: requestId,
            trigger: 'manual',
            expected_generation: expectedGeneration,
            ...(columnSelection ? { column_selection: columnSelection } : {}),
        },
        options,
    );
}

export function disconnectWatchlistM365(expectedGeneration, options) {
    return request(
        'api/watchlist/m365',
        'DELETE',
        { expected_generation: expectedGeneration },
        options,
    );
}

export function isSafeWatchlistM365AuthorizationUrl(value) {
    try {
        const parsed = new URL(String(value || ''));
        return (
            parsed.protocol === 'https:'
            && parsed.origin === 'https://login.microsoftonline.com'
            && parsed.pathname.startsWith('/organizations/')
            && !parsed.username
            && !parsed.password
        );
    } catch (_error) {
        return false;
    }
}

export function isSupportedWatchlistM365SharingUrl(value) {
    try {
        const raw = String(value || '');
        if (!raw || raw !== raw.trim()) {
            return false;
        }
        // Match the backend's exact UTF-8 byte and control-character boundary.
        // encodeURIComponent rejects invalid lone UTF-16 surrogates.
        const encoded = encodeURIComponent(raw);
        if (
            encoded.replace(/%[0-9a-f]{2}|./gi, 'x').length > 2048
            || Array.from(raw).some((character) => {
                const codePoint = character.codePointAt(0);
                return codePoint < 0x20 || codePoint === 0x7f;
            })
        ) {
            return false;
        }
        const parsed = new URL(raw);
        const host = parsed.hostname.toLowerCase().replace(/\.$/, '');
        const path = parsed.pathname.toLowerCase();
        return (
            parsed.protocol === 'https:'
            && (host === 'sharepoint.com' || host.endsWith('.sharepoint.com'))
            && (!parsed.port || parsed.port === '443')
            && !parsed.username
            && !parsed.password
            && !parsed.hash
            && path.includes('/:x:/')
            && !path.includes('/_layouts/')
            && !path.endsWith('/doc.aspx')
            && !path.endsWith('/doc2.aspx')
        );
    } catch (_error) {
        return false;
    }
}

export const WATCHLIST_M365_SETTINGS_ENDPOINTS = Object.freeze([
    'api/watchlist/m365',
    'api/watchlist/m365/authorize',
    'api/watchlist/m365/oauth/complete',
    'api/watchlist/m365/source/inspect',
    'api/watchlist/m365/source',
    'api/watchlist/m365/validate',
]);
